import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { chatService } from "../../services/chat.service";
import { CHAT_STEP } from "../engine/chatStates";
import { inputPolicy } from "../engine/inputPolicy";
import { normalizeChatInput } from "../engine/intentParser";
import { chatErrorText } from "../messages";
import { useDeterministicChat } from "./useDeterministicChat";

export const CONVERSATIONS_KEY = ["me", "chat", "conversations"];

const NOT_RETRYABLE = new Set(["CHAT_DAILY_LIMIT_REACHED"]);
// Refused for what they contain; taken off the screen as well as off the server.
const SENSITIVE_REFUSED = new Set(["CHAT_PIN_NOT_ALLOWED", "CHAT_CARD_DETAILS_NOT_ALLOWED"]);
const PAGE_SIZE = 30;

const CLIENT_STEPS = {
  [CHAT_STEP.GETTING_QUOTE]: "getting_quote",
  [CHAT_STEP.CREATING_TRANSFER]: "preparing_transfer",
  [CHAT_STEP.CONFIRMING_TRANSFER]: "confirming_transfer",
};

let localSequence = 0;

function historyMessage(row) {
  return {
    id: `srv-${row.id}`,
    serverId: row.id,
    sender: row.role === "user" ? "user" : "bot",
    text: row.content,
    status: "sent",
    persisted: true,
  };
}

/**
 * The deterministic chat plus server persistence and the model-assisted
 * reply stream. With no model reply the deterministic parser runs as before.
 */
export function useWebChat() {
  const chat = useDeterministicChat();
  const { dispatch, firstName } = chat;
  const queryClient = useQueryClient();

  const [conversationId, setConversationId] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [serverStep, setServerStep] = useState(null);
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);

  const conversationRef = useRef(null);
  const stepRef = useRef(chat.step);
  const recordedRef = useRef(new Set());
  // "pending" until the list loads, "restoring" while the latest thread
  // loads, "done" once restored or once the customer acts first.
  const restoreRef = useRef("pending");

  stepRef.current = chat.step;
  conversationRef.current = conversationId;

  const conversationsQuery = useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => chatService.listConversations(),
  });

  const refreshConversations = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
    [queryClient]
  );

  const openConversation = useCallback(
    async (id, { isRestore = false } = {}) => {
      if (!isRestore) restoreRef.current = "done";

      const page = await chatService.listMessages(id, { limit: PAGE_SIZE });
      if (isRestore && restoreRef.current !== "restoring") return;
      restoreRef.current = "done";

      const messages = page.messages.map(historyMessage);
      messages.forEach((message) => recordedRef.current.add(message.id));

      setConversationId(id);
      dispatch({
        type: "LOAD_HISTORY",
        messages,
        hasMore: page.has_more,
        firstName,
      });
    },
    [dispatch, firstName]
  );

  /*
   * After a refresh, pick up the most recent conversation once, unless the
   * customer has already started typing in a new one.
   */
  useEffect(() => {
    const latest = conversationsQuery.data?.[0];

    if (restoreRef.current !== "pending" || !conversationsQuery.isSuccess) return;

    if (!latest) {
      restoreRef.current = "done";
      return;
    }

    restoreRef.current = "restoring";
    openConversation(latest.id, { isRestore: true }).catch(() => {
      if (restoreRef.current === "restoring") restoreRef.current = "done";
    });
  }, [conversationsQuery.data, conversationsQuery.isSuccess, openConversation]);

  const ensureConversation = useCallback(async () => {
    if (conversationRef.current) return conversationRef.current;

    const created = await chatService.createConversation({});
    conversationRef.current = created.id;
    setConversationId(created.id);
    return created.id;
  }, []);

  /*
   * Store the lines the deterministic flow showed, so history keeps them.
   */
  useEffect(() => {
    if (!conversationId) return;

    const pending = chat.messages.filter(
      (message) =>
        message.sender === "bot" &&
        !message.persisted &&
        message.text &&
        !recordedRef.current.has(message.id)
    );

    if (!pending.length) return;

    pending.forEach((message) => recordedRef.current.add(message.id));

    chatService
      .recordAssistantMessages(
        conversationId,
        pending.map((message) => ({
          content: message.text,
          kind: message.kind || null,
        }))
      )
      .then(refreshConversations)
      .catch(() => {
        pending.forEach((message) => recordedRef.current.delete(message.id));
      });
  }, [chat.messages, conversationId, refreshConversations]);

  const deliver = useCallback(
    async (localId, text) => {
      setIsPending(true);
      setServerStep(null);

      let accepted = false;
      let outcome = null;

      try {
        const id = await ensureConversation();

        await chatService.streamMessage(
          id,
          { content: text, step: stepRef.current },
          {
            onEvent(name, data) {
              if (name === "accepted") {
                accepted = true;
                dispatch({
                  type: "USER_MESSAGE_SENT",
                  localId,
                  serverId: data.message.id,
                });
              }

              if (name === "step") setServerStep(data.step);
              if (name === "intent") outcome = data;
            },
          }
        );
      } catch (error) {
        if (!accepted) {
          if (SENSITIVE_REFUSED.has(error?.code)) {
            dispatch({
              type: "USER_MESSAGE_REJECTED",
              localId,
              errorText: chatErrorText(error),
            });
          } else {
            dispatch({
              type: "USER_MESSAGE_FAILED",
              localId,
              errorText: chatErrorText(error),
              retryable: !NOT_RETRYABLE.has(error?.code),
            });
          }
          return;
        }
      } finally {
        setIsPending(false);
        setServerStep(null);
      }

      const flow = {
        text,
        recipients: chat.recipients,
        defaultCurrency: chat.defaultCurrency,
        kycStatus: chat.kycStatus,
        isKycApproved: chat.isKycApproved,
      };

      if (outcome?.source === "model") {
        dispatch({
          type: "MODEL_INTENT",
          intent: outcome.intent,
          reply: outcome.reply,
          ...flow,
        });
      } else {
        dispatch({ type: "USER_MESSAGE", alreadyShown: true, ...flow });
      }

      refreshConversations();
    },
    [
      chat.defaultCurrency,
      chat.isKycApproved,
      chat.kycStatus,
      chat.recipients,
      dispatch,
      ensureConversation,
      refreshConversations,
    ]
  );

  const sendMessage = useCallback(
    (value) => {
      // Line breaks are kept for display and storage; the parser normalises its own copy.
      const text = String(value ?? "").trim();
      if (!normalizeChatInput(text) || isPending) return;
      restoreRef.current = "done";

      // Quick-reply actions stay on the device; they are not customer words.
      if (text.startsWith("__")) {
        chat.sendMessage(text);
        return;
      }

      localSequence += 1;
      const localId = `local-${localSequence}`;
      dispatch({ type: "USER_MESSAGE_SENDING", localId, text });
      deliver(localId, text);
    },
    [chat, deliver, dispatch, isPending]
  );

  const retryMessage = useCallback(
    (message) => {
      if (isPending) return;
      dispatch({ type: "USER_MESSAGE_RETRY", localId: message.id });
      deliver(message.id, message.text);
    },
    [deliver, dispatch, isPending]
  );

  const loadEarlier = useCallback(async () => {
    const oldest = chat.messages.find((message) => message.serverId);
    if (!conversationId || !oldest) return;

    setIsLoadingEarlier(true);
    try {
      const page = await chatService.listMessages(conversationId, {
        before: oldest.serverId,
        limit: PAGE_SIZE,
      });
      const messages = page.messages.map(historyMessage);
      messages.forEach((message) => recordedRef.current.add(message.id));
      dispatch({ type: "PREPEND_HISTORY", messages, hasMore: page.has_more });
    } finally {
      setIsLoadingEarlier(false);
    }
  }, [chat.messages, conversationId, dispatch]);

  const startNewConversation = useCallback(() => {
    restoreRef.current = "done";
    setConversationId(null);
    conversationRef.current = null;
    dispatch({ type: "RESET", firstName });
  }, [dispatch, firstName]);

  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => chatService.updateConversation(id, payload),
    onSuccess: refreshConversations,
  });

  const renameConversation = useCallback(
    (id, title) => updateMutation.mutateAsync({ id, title }),
    [updateMutation]
  );

  const archiveConversation = useCallback(
    async (id) => {
      await updateMutation.mutateAsync({ id, archived: true });
      if (id === conversationRef.current) startNewConversation();
    },
    [startNewConversation, updateMutation]
  );

  const activeStep = useMemo(() => {
    if (isPending) return serverStep || "typing";
    return CLIENT_STEPS[chat.step] || null;
  }, [chat.step, isPending, serverStep]);

  return {
    ...chat,
    composerPlaceholder: inputPolicy(chat.step).secret
      ? "chat.composer.placeholder_secure"
      : "chat.composer.placeholder",
    sendMessage,
    retryMessage,
    isPending,
    activeStep,

    conversationId,
    conversations: conversationsQuery.data || [],
    isLoadingConversations: conversationsQuery.isLoading,
    conversationsError: conversationsQuery.error,
    openConversation,
    startNewConversation,
    renameConversation,
    archiveConversation,

    loadEarlier,
    isLoadingEarlier,
  };
}
